// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  ConnectionMonitorResult,
  ConnectionMonitorsListOptionalParams,
  ConnectionMonitor,
  ConnectionMonitorsCreateOrUpdateOptionalParams,
  ConnectionMonitorsCreateOrUpdateResponse,
  ConnectionMonitorsGetOptionalParams,
  ConnectionMonitorsGetResponse,
  ConnectionMonitorsDeleteOptionalParams,
  TagsObject,
  ConnectionMonitorsUpdateTagsOptionalParams,
  ConnectionMonitorsUpdateTagsResponse,
  ConnectionMonitorsStopOptionalParams,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a ConnectionMonitors. */
export interface ConnectionMonitors {
  /**
   * Lists all connection monitors for the specified Network Watcher.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    networkWatcherName: string,
    options?: ConnectionMonitorsListOptionalParams,
  ): PagedAsyncIterableIterator<ConnectionMonitorResult>;
  /**
   * Create or update a connection monitor.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param connectionMonitorName The name of the connection monitor.
   * @param parameters Parameters that define the operation to create a connection monitor.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    parameters: ConnectionMonitor,
    options?: ConnectionMonitorsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ConnectionMonitorsCreateOrUpdateResponse>,
      ConnectionMonitorsCreateOrUpdateResponse
    >
  >;
  /**
   * Create or update a connection monitor.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param connectionMonitorName The name of the connection monitor.
   * @param parameters Parameters that define the operation to create a connection monitor.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    parameters: ConnectionMonitor,
    options?: ConnectionMonitorsCreateOrUpdateOptionalParams,
  ): Promise<ConnectionMonitorsCreateOrUpdateResponse>;
  /**
   * Gets a connection monitor by name.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param connectionMonitorName The name of the connection monitor.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    options?: ConnectionMonitorsGetOptionalParams,
  ): Promise<ConnectionMonitorsGetResponse>;
  /**
   * Deletes the specified connection monitor.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param connectionMonitorName The name of the connection monitor.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    options?: ConnectionMonitorsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes the specified connection monitor.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param connectionMonitorName The name of the connection monitor.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    options?: ConnectionMonitorsDeleteOptionalParams,
  ): Promise<void>;
  /**
   * Update tags of the specified connection monitor.
   * @param resourceGroupName The name of the resource group.
   * @param networkWatcherName The name of the network watcher.
   * @param connectionMonitorName The name of the connection monitor.
   * @param parameters Parameters supplied to update connection monitor tags.
   * @param options The options parameters.
   */
  updateTags(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    parameters: TagsObject,
    options?: ConnectionMonitorsUpdateTagsOptionalParams,
  ): Promise<ConnectionMonitorsUpdateTagsResponse>;
  /**
   * Stops the specified connection monitor.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param connectionMonitorName The name of the connection monitor.
   * @param options The options parameters.
   */
  beginStop(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    options?: ConnectionMonitorsStopOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Stops the specified connection monitor.
   * @param resourceGroupName The name of the resource group containing Network Watcher.
   * @param networkWatcherName The name of the Network Watcher resource.
   * @param connectionMonitorName The name of the connection monitor.
   * @param options The options parameters.
   */
  beginStopAndWait(
    resourceGroupName: string,
    networkWatcherName: string,
    connectionMonitorName: string,
    options?: ConnectionMonitorsStopOptionalParams,
  ): Promise<void>;
}
